import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ListShell } from "@/components/shared/ListShell";
import { ListToolbar } from "@/components/shared/ListToolbar";
import { ListPagination } from "@/components/shared/ListPagination";
import {
  DataTable,
  type DataTableColumn,
  type SelectionMode,
} from "@/components/shared/DataTable";
import { SelectionModeControl } from "@/components/shared/SelectionModeControl";
import {
  useBookingList,
  computeBookingTotal,
  type BookingRow,
} from "@/hooks/useBookings";
import { BookingStatus } from "@/hooks/useBookings";
import { formatDate, formatCurrency } from "@/utils/format";
import { downloadCsv } from "@/utils/exportCsv";
import { useAuth } from "@/contexts/AuthContext";

type TabKey = "all" | BookingStatus;

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: "all", label: "All" },
  { key: BookingStatus.PENDING, label: "Pending" },
  { key: BookingStatus.CONFIRMED, label: "Confirmed" },
  { key: BookingStatus.ACTIVE, label: "Active" },
  { key: BookingStatus.COMPLETED, label: "Completed" },
  { key: BookingStatus.CANCELLED, label: "Cancelled" },
];

const DEFAULT_PAGE_SIZE = 50;

const latestPayment = (b: BookingRow) => b.payments?.[0];

const exportRows = (rows: BookingRow[]) =>
  downloadCsv(
    "bookings",
    ["Ref", "Centre", "Check-in", "Status", "Amount", "Payment"],
    rows.map((b) => {
      const p = latestPayment(b);
      return [
        b.bookingRef,
        b.business?.nameEn ?? "",
        formatDate(b.checkinDate),
        String(b.status),
        String(computeBookingTotal(b)),
        p?.status ?? "Unpaid",
      ];
    }),
  );

export const BookingsList = () => {
  const [tab, setTab] = useState<TabKey>("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [selection, setSelection] = useState<SelectionMode>("none");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { can } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useBookingList({
    status: tab === "all" ? undefined : (tab as BookingStatus),
    page,
    limit,
  });
  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const setSelectionMode = (m: SelectionMode) => {
    setSelection(m);
    setSelectedIds([]);
  };

  const columns: Array<DataTableColumn<BookingRow>> = [
    {
      key: "ref",
      header: "Ref",
      render: (b) => <span className="font-mono text-xs">{b.bookingRef}</span>,
    },
    {
      key: "centre",
      header: "Centre",
      render: (b) => b.business?.nameEn ?? "—",
    },
    {
      key: "customer",
      header: "Customer",
      render: (b) => b.customer?.name ?? b.customer?.user?.phone ?? "—",
    },
    {
      key: "checkin",
      header: "Check-in",
      render: (b) => (
        <span className="text-muted-foreground">
          {formatDate(b.checkinDate)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (b) => <StatusBadge status={b.status} />,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (b) => (
        <span className="font-medium">
          {formatCurrency(computeBookingTotal(b))}
        </span>
      ),
    },
    {
      key: "payment",
      header: "Payment",
      render: (b) => {
        const p = latestPayment(b);
        return p ? (
          <StatusBadge status={p.status} />
        ) : (
          <span className="text-muted-foreground">Unpaid</span>
        );
      },
    },
  ];

  return (
    <AppLayout title="Bookings">
      <ListToolbar<TabKey, string>
        tabs={{
          value: tab,
          onChange: (v) => {
            setTab(v);
            setPage(1);
          },
          options: tabs.map((t) => ({ key: t.key, label: t.label })),
        }}
        onExport={
          can("bookings", "export") ? () => exportRows(rows) : undefined
        }
        actions={
          <SelectionModeControl value={selection} onChange={setSelectionMode} />
        }
      />

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-btn px-4 py-2 mb-3 text-sm">
          <span className="font-medium">{selectedIds.length} selected</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                toast.success(
                  `Refund initiated for ${selectedIds.length} bookings`,
                );
                setSelectedIds([]);
              }}
              className="px-3 py-1 rounded-btn border border-border hover:bg-muted"
            >
              Refund
            </button>
            <button
              type="button"
              onClick={() => {
                toast.error(`Cancelled ${selectedIds.length} bookings`);
                setSelectedIds([]);
              }}
              className="px-3 py-1 rounded-btn border border-border hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="px-3 py-1 rounded-btn text-muted-foreground hover:bg-muted"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={refetch}
        emptyTitle="No bookings"
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(b) => b.id}
          selection={selection}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onRowClick={(b) => navigate(`/bookings/${b.id}`)}
        />
        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="bookings"
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(n) => {
            setLimit(n);
            setPage(1);
          }}
        />
      </ListShell>
    </AppLayout>
  );
};

export default BookingsList;
